import { useCallback } from 'react';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import type { CreateEventFeature } from '@shared/constants/customPageMigration';
import { extractMigratedPageFeatures } from '@shared/constants/customPageMigration';

/**
 * Returns a function that computes the eventFeatures array based on
 * currently enabled feature flags. These features are passed to the
 * backend during event creation to configure the new event.
 */
export const useCreateEventFeatures = () => {
  const { value: websiteAllPagesAsCustomPagesValue, payload: websiteAllPagesAsCustomPagesPayload } = useFeatureValue('websiteAllPagesAsCustomPagesEnabled');
  const isWebsiteAllPagesAsCustomPagesEnabled = websiteAllPagesAsCustomPagesValue === 'on' || websiteAllPagesAsCustomPagesValue === 'onjoysuperuser';
  const [isSunsetVideoPageEnabled] = useIsFeatureEnabled('sunsetVideoPageEnabled');
  const [isEngagementAlbumEnabled] = useIsFeatureEnabled('websitePhotosEngagementAlbumEnable');
  const [isPhotoMonetizationEnabled] = useIsFeatureEnabled('websitePhotoMonetizationEnabled');

  const getEventFeatures = useCallback((): ReadonlyArray<CreateEventFeature> => {
    // Theme customization restrictions will be removed for all newly created events. Per-Guest Page
    // Visibility (PRNT-4332) is provisioned for all too; the BE allowlist limits it to non-pro types.
    // The unified website editor experience is now fully rolled out, so new events always get it.
    const features: Array<CreateEventFeature> = ['bypassWebsiteThemeCustomization', 'websitepageperguestvisibility', 'useGuestPageContentRedesign', 'useUnifiedWebsiteEditor'];

    // Gated so the BE-backed default engagement album has a remote kill-switch
    // (the BE field may not be live yet — keep the flag off until it ships).
    if (isEngagementAlbumEnabled) {
      features.push('enableDefaultEngagementMediaCollection');
    }

    // Only derive per-page event features when the top-level remote flag is enabled.
    if (isWebsiteAllPagesAsCustomPagesEnabled) {
      features.push(...extractMigratedPageFeatures(websiteAllPagesAsCustomPagesPayload, 'migratedPages'));
    }

    // Sunset (PRNT-4148): persist a per-event flag at create time so the legacy Video page
    // is permanently hidden for new events. Decision is captured here and never re-evaluated.
    if (isSunsetVideoPageEnabled) {
      features.push('sunsetVideoPageEnabled');
    }

    if (isPhotoMonetizationEnabled) {
      features.push('usePhotosMonetization');
    }

    return features;
  }, [isWebsiteAllPagesAsCustomPagesEnabled, isSunsetVideoPageEnabled, isEngagementAlbumEnabled, isPhotoMonetizationEnabled, websiteAllPagesAsCustomPagesPayload]);

  return { getEventFeatures };
};
